import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, shareReplay, tap } from 'rxjs';

import { apiUrl } from '../config/api-endpoints';
import { SKIP_ERROR_HANDLING } from '../http';
import {
  PortalUser,
  PortalUserBranchFilterOption,
  PortalUserCityListResponse,
  PortalUserCreatePayload,
  PortalUserCreateResponse,
  PortalUserCountsResponse,
  PortalUserListQuery,
  PortalUserListResponse,
  PortalUserDetail,
  PortalUserDeactivatePayload,
  PortalUserStatusResponse,
  PortalUserUpdatePayload,
} from '../../features/user-role-management/models/user-management-api';
import {
  ClientListQuery,
  ClientRead,
  PaginatedClientsResponse,
} from '../../features/client-management/models/client-management';
import {
  VendorListParams,
  VendorListResponse,
} from '../../features/vendor-management/models/vendor-management-row';

@Injectable({ providedIn: 'root' })
export class UserManagementService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = apiUrl('/portal/users');
  private readonly managedErrorContext = new HttpContext().set(SKIP_ERROR_HANDLING, true);
  private readonly userDetailCache = new Map<string, Observable<PortalUserDetail>>();

  listUsers(query: PortalUserListQuery): Observable<PortalUserListResponse> {
    return this.http.post<PortalUserListResponse>(`${this.baseUrl}/list`, this.buildPayload(query));
  }

  createUser(payload: PortalUserCreatePayload): Observable<PortalUserCreateResponse> {
    return this.http.post<PortalUserCreateResponse>(this.baseUrl, payload);
  }

  listUserClients(query: ClientListQuery): Observable<PaginatedClientsResponse> {
    const payload: Record<string, string | number | undefined> = {
      page: query.page,
      page_size: query.pageSize,
      search: query.search,
      cities: query.cities?.join(','),
      statuses: query.statuses?.join(','),
      onboarded_from: query.onboardedFrom,
      onboarded_to: query.onboardedTo,
    };
    for (const key of Object.keys(payload)) {
      if (payload[key] === undefined || `${payload[key]}`.trim() === '') {
        delete payload[key];
      }
    }
    return this.http.post<PaginatedClientsResponse>(`${this.baseUrl}/client/search`, payload);
  }

  getUserClient(clientId: string): Observable<ClientRead> {
    return this.http.get<ClientRead>(`${this.baseUrl}/client/${encodeURIComponent(clientId)}`);
  }

  listUserVendors(params: VendorListParams): Observable<VendorListResponse> {
    const payload: Record<string, string | number> = {
      page: params.page,
      limit: params.pageSize,
    };
    if (params.search?.trim()) payload['search'] = params.search.trim();
    if (params.statuses?.length) payload['statuses'] = params.statuses.join(',');
    if (params.onboardedFrom) payload['onboarded_from'] = params.onboardedFrom;
    if (params.onboardedTo) payload['onboarded_to'] = params.onboardedTo;
    return this.http.post<VendorListResponse>(`${this.baseUrl}/vendor/search`, payload);
  }

  getUser(userId: string): Observable<PortalUserDetail> {
    const normalizedUserId = userId.trim();
    const cached = this.userDetailCache.get(normalizedUserId);
    if (cached) {
      return cached;
    }

    const request = this.http
      .get<PortalUserDetail>(`${this.baseUrl}/${encodeURIComponent(normalizedUserId)}`)
      .pipe(shareReplay({ bufferSize: 1, refCount: false }));
    this.userDetailCache.set(normalizedUserId, request);
    return request;
  }

  updateUser(userId: string, payload: PortalUserUpdatePayload): Observable<PortalUser> {
    return this.http
      .patch<PortalUser>(`${this.baseUrl}/${encodeURIComponent(userId)}`, payload)
      .pipe(tap(() => this.userDetailCache.delete(userId.trim())));
  }

  getUserCounts(): Observable<PortalUserCountsResponse> {
    return this.http.get<PortalUserCountsResponse>(`${this.baseUrl}/count`);
  }

  listUserCities(): Observable<PortalUserCityListResponse> {
    return this.http.get<PortalUserCityListResponse>(`${this.baseUrl}/cities`);
  }

  listUserBranchFilterOptions(): Observable<PortalUserBranchFilterOption[]> {
    return this.http.get<PortalUserBranchFilterOption[]>(`${this.baseUrl}/filter-options/branches`);
  }

  listUserTypeFilterOptions(): Observable<string[]> {
    return this.http.get<string[]>(`${this.baseUrl}/filter-options/user-types`);
  }

  downloadUsers(query: PortalUserListQuery): Observable<Blob> {
    return this.http.post(`${this.baseUrl}/list/download`, this.buildPayload(query), {
      responseType: 'blob',
    });
  }

  markUserInactive(
    userId: string,
    payload: PortalUserDeactivatePayload,
  ): Observable<PortalUserStatusResponse> {
    return this.http
      .post<PortalUserStatusResponse>(
        `${this.baseUrl}/${encodeURIComponent(userId)}/inactive`,
        payload,
        { context: this.managedErrorContext },
      )
      .pipe(tap(() => this.userDetailCache.delete(userId.trim())));
  }

  markUserActive(userId: string): Observable<PortalUserStatusResponse> {
    return this.http
      .post<PortalUserStatusResponse>(`${this.baseUrl}/${encodeURIComponent(userId)}/active`, {})
      .pipe(tap(() => this.userDetailCache.delete(userId.trim())));
  }

  private buildPayload(query: PortalUserListQuery): PortalUserListQuery {
    const filters = query.filters ?? {};
    const payloadFilters: NonNullable<PortalUserListQuery['filters']> = {};

    if (filters.search?.trim()) {
      payloadFilters.search = filters.search.trim();
    }

    if (filters.statuses?.length) {
      payloadFilters.statuses = filters.statuses;
    }

    if (filters.role_ids?.length) {
      payloadFilters.role_ids = filters.role_ids;
    }

    if (filters.policy_ids?.length) {
      payloadFilters.policy_ids = filters.policy_ids;
    }

    if (filters.cities?.length) {
      payloadFilters.cities = filters.cities.map((city) => city.trim()).filter(Boolean);
    }

    if (filters.user_types?.length) {
      payloadFilters.user_types = filters.user_types;
    }

    if (filters.client_branch_ids?.length) {
      payloadFilters.client_branch_ids = filters.client_branch_ids;
    }

    if (filters.vendor_ids?.length) {
      payloadFilters.vendor_ids = filters.vendor_ids;
    }

    if (filters.updated_from) {
      payloadFilters.updated_from = filters.updated_from;
    }

    if (filters.updated_to) {
      payloadFilters.updated_to = filters.updated_to;
    }

    return {
      page: query.page,
      limit: query.limit,
      filters: payloadFilters,
      ...(query.sort ? { sort: query.sort } : {}),
    };
  }
}
